import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import { triggerConfetti } from './confetti';

// Training quiz widget. A DJ short on training-Zoom time can take this instead -
// Quickbase credits them the time once they pass, and passing means every answer
// right. Scoring is entirely Quickbase's: we post the answers and read back the
// result, so nothing here decides whether an answer was right, whether the DJ
// passed, or whether they needed the quiz at all. What this file does own is
// the review: a DJ who fell short sees every question with their answer and the
// right one before they retake.
export default function TrainingQuizSection({ djElectionRid }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const fetchQuiz = useCallback(async () => {
    if (!djElectionRid) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/getTrainingQuiz?dj_election_rid=${djElectionRid}`
      );
      setQuiz(res.data);
      setLoadError(null);
    } catch (err) {
      console.error('Error fetching training quiz:', err);
      setLoadError("We couldn't load your training quiz. Refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  }, [djElectionRid]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  const handleClose = (result = { success: false }) => {
    setIsPopupOpen(false);
    // The score lives in Quickbase, so re-read rather than trusting local state.
    if (result.success) fetchQuiz();
  };

  if (loading || !djElectionRid) return null;

  if (loadError) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-200 mb-6" id="training-quiz-section">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Training Quiz</h3>
        <p className="text-sm text-red-600">{loadError}</p>
      </div>
    );
  }

  if (!quiz?.success) return null;

  // Whether this DJ sees the quiz at all is Quickbase's call, made twice before
  // this line: the Widget Layout table decides which statuses get the widget,
  // and the election's Training Quiz Live checkbox - show_quiz here - decides
  // whether the quiz is on. Off means nothing renders, not even a score they
  // already earned.
  if (!quiz.show_quiz) return null;

  const { completed, passed, review, questions, latest_attempt, time_in_training } = quiz;

  // A quiz with no questions behind it can't be taken. Say so rather than
  // rendering a button that leads to an empty modal.
  const hasQuestions = Array.isArray(questions) && questions.length > 0;
  const hasReview = Array.isArray(review) && review.length > 0;

  // One state drives the pill, the status cell, the copy and the button, so
  // they can't disagree. "Passed" is Quickbase's word - the Passed Training
  // Quiz summary on the DJ - Election, true once any attempt scored 100%.
  const status = passed ? STATUS.passed : completed ? STATUS.not_passed : STATUS.not_taken;

  // Everything in the grid reads off the most recent attempt, so the score,
  // the count and the date can't disagree with each other.
  const toPercentage = (fraction) =>
    Number.isFinite(fraction) ? `${Math.round(fraction * 100)}%` : '—';
  const correct = latest_attempt?.total_correct_answers;
  const total = latest_attempt?.total_number_of_questions;

  // The not-taken and not-passed copy both close on the training materials,
  // the DJ Essentials page, spelled out as its URL.
  const trainingMaterials = (
    <>
      {"If you'd like to review before you take the quiz, you can find the training materials at "}
      <a
        href={TRAINING_MATERIALS_URL}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-primary underline break-all hover:text-primary-dark"
      >
        {TRAINING_MATERIALS_URL}
      </a>
      .
    </>
  );

  const instructions = passed ? (
    'You passed the training quiz with every answer right. Nothing more to do here.'
  ) : completed ? (
    <>
      In order to get paid, you must RE-TAKE the training quiz before Election Day. You need a 100%
      to pass, and you can take it as many times as you need. {trainingMaterials}
    </>
  ) : hasQuestions ? (
    <>
      In order to get paid, you must take the training quiz before Election Day. It should only take
      2-3 minutes. You need a 100% to pass, and you can take it as many times as you need.{' '}
      {trainingMaterials}
    </>
  ) : (
    "The training quiz isn't ready yet. Check back shortly — we'll have it up soon."
  );

  // A passed DJ has nothing left to take. Everyone else gets in as long as
  // there's something to open - a review to read or a quiz to take.
  const canOpen = !passed && (hasQuestions || hasReview);
  const buttonLabel = !completed ? 'Start Quiz' : hasReview ? 'Review & Retake' : 'Retake Quiz';

  return (
    <div className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-200 mb-6" id="training-quiz-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-900">Training Quiz</h3>
        <span className={`px-3 py-1 text-sm font-medium rounded-lg ${status.pill}`}>
          {status.label}
        </span>
      </div>

      <div className="space-y-6">
        {completed ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <InfoField label="Status" value={status.label} valueClassName={status.text} />
            <InfoField label="Score" value={toPercentage(latest_attempt?.result)} />
            <InfoField
              label="Correct Answers"
              value={
                Number.isFinite(correct) && Number.isFinite(total) && total > 0
                  ? `${correct} of ${total}`
                  : '—'
              }
            />
            <InfoField label="Test Date" value={toTestDate(latest_attempt?.taken_date)} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <InfoField label="Status" value={status.label} valueClassName={status.text} />
            <InfoField label="Questions" value={hasQuestions ? questions.length : '—'} />
            {/* Quickbase's resolved figure (Formula Time In Training). How much is
                enough is its rule too, so no target is shown against it. */}
            <InfoField label="Training Time" value={`${time_in_training} mins`} />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Instructions</label>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">{instructions}</p>
          </div>
        </div>

        {canOpen && (
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 shadow-sm"
            >
              {completed ? (
                <svg className="mr-2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="mr-2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              )}
              {buttonLabel}
            </button>
          </div>
        )}
      </div>

      {isPopupOpen && canOpen && (
        <TrainingQuizPopup
          djElectionRid={djElectionRid}
          questions={hasQuestions ? questions : []}
          review={hasReview ? review : null}
          onClose={handleClose}
        />
      )}
    </div>
  );
}

// The DJ Essentials page for this cycle, the same page the header's DJ
// Essentials button links to.
const TRAINING_MATERIALS_URL = 'https://djsatthepolls.org/djs2026/#essentials';

// The three states the card can be in. Green and red are the same pair the
// review uses for right and wrong answers, so the pill up top and the answers
// in the modal speak the same colour.
const STATUS = {
  passed: { label: 'Passed', pill: 'text-green-800 bg-green-100', text: 'text-green-600' },
  not_passed: { label: 'Not Passed', pill: 'text-red-800 bg-red-100', text: 'text-red-600' },
  not_taken: { label: 'Not Taken', pill: 'text-yellow-800 bg-yellow-100', text: 'text-yellow-600' },
};

// Quickbase sends the attempt timestamp as "MM-DD-YYYY hh:mm AM". Only the day
// matters here, in the same MM-DD-YYYY the Contract card uses.
function toTestDate(value) {
  if (!value) return '—';
  const parsed = moment(value, 'MM-DD-YYYY hh:mm A', true);
  return parsed.isValid() ? parsed.format('MM-DD-YYYY') : value;
}

// Sub-headed value cell, matching the Contract Details grid.
function InfoField({ label, value, valueClassName = 'text-gray-900' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <p className={`mt-1 text-sm ${valueClassName}`}>{value}</p>
    </div>
  );
}

// Two modes in one dialog. 'quiz' is an attempt in progress, one question at a
// time. 'review' is the last graded attempt laid out in full. A DJ who hasn't
// passed opens on the review and reaches the quiz through "Retake quiz" - the
// review is the step before the retake, not a detour from it - and a failed
// submit lands back on the review with the fresh grading.
function TrainingQuizPopup({ djElectionRid, questions, review: initialReview, onClose }) {
  const [mode, setMode] = useState(initialReview ? 'review' : 'quiz');
  const [review, setReview] = useState(initialReview);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Whether this dialog has written an attempt to Quickbase. The widget only
  // re-reads its score on close when there's something new to read.
  const [attemptRecorded, setAttemptRecorded] = useState(false);

  const canRetake = questions.length > 0;
  const question = questions[current];
  const isLast = current === questions.length - 1;
  const selected = question ? answers[question.rid] : undefined;

  const close = useCallback(() => {
    onClose({ success: attemptRecorded });
  }, [onClose, attemptRecorded]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close, submitting]);

  const handleSelect = (choice) => {
    setAnswers((prev) => ({ ...prev, [question.rid]: choice }));
    setError('');
  };

  const handleNext = () => {
    if (!selected) {
      setError('Pick an answer to continue.');
      return;
    }
    setError('');
    setCurrent((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrent((prev) => prev - 1);
  };

  const handleRetake = () => {
    setAnswers({});
    setCurrent(0);
    setError('');
    setMode('quiz');
  };

  const handleSubmit = async () => {
    if (!selected) {
      setError('Pick an answer to continue.');
      return;
    }
    try {
      setSubmitting(true);
      // The answer text goes back verbatim - Quickbase grades by comparing it to
      // the question's correct answer.
      const payload = {
        dj_election_rid: djElectionRid,
        answers: questions.map((q) => ({ question_rid: q.rid, answer: answers[q.rid] }))
      };
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/submitTrainingQuiz`,
        payload
      );
      if (res.data.success) {
        setAttemptRecorded(true);
        if (res.data.attempt?.passed) {
          triggerConfetti();
          onClose({ success: true });
          return;
        }
        // Short of 100%: show the grading straight away rather than closing on
        // a score, so the retake starts from what went wrong.
        setReview(Array.isArray(res.data.review) ? res.data.review : []);
        setMode('review');
        setSubmitting(false);
      } else {
        setError(res.data.message || "We couldn't save your answers. Try submitting again.");
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Error submitting training quiz:', err);
      setError(
        err.response?.data?.message || "We couldn't save your answers. Try submitting again."
      );
      setSubmitting(false);
    }
  };

  const reviewing = mode === 'review' && Array.isArray(review);
  const correctCount = reviewing ? review.filter((item) => item.is_correct).length : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="training-quiz-title"
        className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto relative flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        <div className="p-6 border-b border-gray-100 rounded-t-2xl">
          <h2 id="training-quiz-title" className="text-2xl font-semibold text-gray-900">
            Training Quiz
          </h2>
          {/* One segment per question. While the quiz runs it fills as questions
              are answered, doubling as the progress bar and a map of what's
              left. On the review the same segments turn green or red, so the
              score is readable before a word of it is. */}
          <div className="mt-4 flex gap-1.5" aria-hidden="true">
            {reviewing
              ? review.map((item) => (
                  <span
                    key={item.question_rid}
                    className={`h-1.5 flex-1 rounded-full ${item.is_correct ? 'bg-green-500' : 'bg-red-400'}`}
                  />
                ))
              : questions.map((q, index) => (
                  <span
                    key={q.rid}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      answers[q.rid]
                        ? 'bg-primary'
                        : index === current
                        ? 'bg-primary/30'
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
          </div>
          {/* The review only opens for an attempt that fell short, so it always
              leads with the message to go back over it. */}
          {reviewing && (
            <p className="mt-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm font-medium">
              Bummer! You didn't get them all right. Scroll through to see what you missed and retake the test.
            </p>
          )}
          <p className="mt-3 text-sm text-gray-500">
            {reviewing
              ? `${correctCount} of ${review.length} correct. You need all ${review.length} to pass.`
              : `Question ${current + 1} of ${questions.length}`}
          </p>
          <button
            onClick={close}
            disabled={submitting}
            aria-label="Close quiz"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {reviewing ? (
            <ReviewList review={review} />
          ) : (
            <fieldset>
              {/* whitespace-pre-line keeps the line breaks staff author into the
                  question (a multi-line text field in Quickbase). */}
              <legend className="block text-lg font-medium text-gray-900 mb-4 whitespace-pre-line">
                {question.question}
              </legend>
              <div className="space-y-3">
                {question.choices.map((choice) => (
                  <label
                    key={choice}
                    className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                      selected === choice ? 'border-primary bg-primary/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question_${question.rid}`}
                      checked={selected === choice}
                      onChange={() => handleSelect(choice)}
                      className="mt-0.5 h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">{choice}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <p>{error}</p>
            </div>
          )}
        </div>

        <div className="mt-auto border-t border-gray-200 p-4 flex justify-between rounded-b-2xl">
          {reviewing ? (
            <>
              <button
                onClick={close}
                className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Close
              </button>
              {canRetake && (
                <button
                  onClick={handleRetake}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Retake quiz
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                disabled={current === 0 || submitting}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  current === 0 || submitting
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Back
              </button>
              {isLast ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    submitting ? 'bg-primary/70 text-white cursor-wait' : 'bg-primary text-white hover:bg-primary-dark'
                  }`}
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit answers'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark text-sm font-medium"
                >
                  Next
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Quickbase grades by exact text match against the question's correct answer.
// Matching for *display* is looser than that on purpose: trimmed and
// case-insensitive, so an authored "10 Feet" still finds the "10 feet" choice
// to mark. Which answer was right is never decided here - that's is_correct,
// straight from Quickbase's Score.
function sameText(a, b) {
  return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
}

// Every question from the attempt in quiz order, numbered the way the DJ met
// them ("Question 3 of 12"), with the number tinted by the result so the missed
// ones stand out down the list before any reading.
function ReviewList({ review }) {
  return (
    <ol className="space-y-6">
      {review.map((item, index) => (
        <li key={item.question_rid}>
          <div className="flex items-start gap-3">
            <span
              className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                item.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <p className="text-sm font-medium text-gray-900 pt-0.5">
              <span className="sr-only">{item.is_correct ? 'Correct. ' : 'Incorrect. '}</span>
              <span className="whitespace-pre-line">{item.question}</span>
            </p>
          </div>
          <ReviewChoices item={item} />
        </li>
      ))}
    </ol>
  );
}

function ReviewChoices({ item }) {
  const { choices, selected_answer, correct_answer, is_correct } = item;

  // The correct answer is normally one of the rows below and gets marked there.
  // When it isn't - the question's choices changed since the attempt, or the
  // authored correct answer doesn't match any choice - or when the DJ's own row
  // is the one that matches yet Quickbase graded it wrong, spell it out under
  // the list so the DJ is never left without the right answer.
  const correctMarked = choices.some(
    (choice) => sameText(choice, correct_answer) && (is_correct || !sameText(choice, selected_answer))
  );

  return (
    <div className="mt-3 ml-9 space-y-2">
      {choices.map((choice) => (
        <ReviewChoice
          key={choice}
          text={choice}
          selected={sameText(choice, selected_answer)}
          correct={sameText(choice, correct_answer)}
          isCorrect={is_correct}
        />
      ))}
      {choices.length === 0 && (
        <p className="text-sm text-gray-700">
          <span className="font-medium">Your answer:</span> {selected_answer || '—'}
        </p>
      )}
      {!correctMarked && (
        <p className="text-sm text-green-800">
          <span className="font-medium">Correct answer:</span> {correct_answer || '—'}
        </p>
      )}
    </div>
  );
}

// One choice as it stands after grading. Filled green is the DJ's answer and it
// was right; filled red is the DJ's answer and it wasn't; outlined green is the
// right answer they didn't pick. The tag says whose it is, the icon says right
// or wrong, so neither colour has to carry both.
function ReviewChoice({ text, selected, correct, isCorrect }) {
  let tone = 'border-gray-100 text-gray-500';
  let tag = null;

  if (selected && isCorrect) {
    tone = 'border-green-300 bg-green-50 text-green-900 font-medium';
    tag = <ReviewTag right>Your answer</ReviewTag>;
  } else if (selected) {
    tone = 'border-red-300 bg-red-50 text-red-900 font-medium';
    tag = <ReviewTag>Your answer</ReviewTag>;
  } else if (correct) {
    tone = 'border-green-300 text-gray-900';
    tag = <ReviewTag right>Correct answer</ReviewTag>;
  }

  return (
    <div className={`flex items-start justify-between gap-3 p-3 border rounded-lg ${tone}`}>
      <span className="text-sm">{text}</span>
      {tag}
    </div>
  );
}

function ReviewTag({ right = false, children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 flex-shrink-0 text-xs font-medium whitespace-nowrap ${
        right ? 'text-green-700' : 'text-red-700'
      }`}
    >
      {right ? (
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      {children}
    </span>
  );
}
